import { Action, Dispatch } from "redux"
import { mapListToCamelCase, mapToCamelCase } from "../utility"
import { handleAuthorizedRequest, trends } from "../apis"
import { lang } from "../constants/languages"
import { SortParams, TrendFilter } from "../types"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

export const TRENDS_ACTION = {
  FETCH_FILTER_OPTIONS: "@TREND/FETCH_FILTER_OPTIONS",
  FETCH_STREAMS_DAILY: "@TREND/FETCH_STREAMS_DAILY",
  FETCH_STREAMS_COUNTRY: "@TREND/FETCH_STREAMS_COUNTRY",
  FETCH_STREAMS_DEMOGRAPHIC: "@TREND/FETCH_STREAMS_DEMOGRAPHIC",
  FETCH_TREND_TOP_TRACKS: "@TREND/FETCH_TREND_TOP_TRACKS",
  FETCH_STREAMS_TRACK_MONTHS: "@TREND/FETCH_STREAMS_TRACK_MONTHS",
  FETCH_ARTIST_TREND_ECONOMIES: "@TREND/FETCH_ARTIST_TREND_ECONOMIES",
  FETCH_SUMMARY_TRACK_ROYALTIES: "@TREND/FETCH_SUMMARY_TRACK_ROYALTIES",
  FETCH_SUMMARY_TRACK_STREAMS: "@TREND/FETCH_SUMMARY_TRACK_STREAMS"
}

const formatDate = (date?: Date) => {
  if (!date) {
    return ""
  }

  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}${month}${day}`
}

const buildParams = (filter: TrendFilter, addEventType?: boolean) => {
  const startDateParam =
    filter.dateValues && filter.dateValues?.[0] ? `start_date=${formatDate(filter.dateValues[0])}` : ""
  const endDateParam =
    filter.dateValues && filter.dateValues?.[1] ? `&end_date=${formatDate(filter.dateValues[1])}` : ""
  const isrcParam = filter.isrcList?.length > 0 ? `&isrc_list=${filter.isrcList.join(",")}` : ""
  const countryParam = filter.countryList?.length > 0 ? `&country_list=${filter.countryList.join(",")}` : ""
  const sexParam = filter.sexList?.length > 0 ? `&sex_list=${filter.sexList.join(",")}` : ""
  const ageGroupParam = filter.ageGroupList?.length > 0 ? `&age_group_list=${filter.ageGroupList.join(",")}` : ""
  const platformListParam = filter.platformList?.length > 0 ? `&platform_list=${filter.platformList.join(",")}` : ""
  const artistListParam = filter.artistList?.length > 0 ? `&artist_list=${filter.artistList.join(",")}` : ""
  const userIdsParam = (filter.selectedUsers || []).length > 0 ? `&user_ids=${filter.selectedUsers?.join(",")}` : ""
  const isAdminParam = filter.isAdmin ? `&is_admin=1` : ""
  const eventTypeParam =
    filter.eventType === "social"
      ? "&event_type=social_production,social_consumption"
      : `&event_type=${filter.eventType}`

  return `?${startDateParam}${endDateParam}${isrcParam}${countryParam}${sexParam}${ageGroupParam}${artistListParam}${userIdsParam}${platformListParam}${isAdminParam}${
    addEventType ? eventTypeParam : ""
  }`
}

export const fetchStreamsDaily = (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await trends(`/streams_daily/${buildParams(filter, true)}`, "get")

      dispatch({
        type: TRENDS_ACTION.FETCH_STREAMS_DAILY,
        payload: mapListToCamelCase(response.data.results)
      })

      if (fetchOptions) {
        dispatch({
          type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
          payload: mapToCamelCase(response.data.options)
        })
      }
    },
    lang.trends.error_on_streams_daily,
    dispatch
  )
}

export const fetchStreamsCountry =
  (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await trends(`/streams_country/${buildParams(filter, true)}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_COUNTRY,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      lang.trends.error_on_streams_country,
      dispatch
    )
  }

export const fetchStreamsDemographic =
  (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await trends(`/streams_demographic/${buildParams(filter, true)}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_DEMOGRAPHIC,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      lang.trends.error_on_streams_demographic,
      dispatch
    )
  }

export const fetchTrendTopTracks =
  (date: string, fetchOptions: boolean, filter: TrendFilter) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const platformFilter = filter.platformList ? `&platform_list=${filter.platformList.join(",")}` : ""
        const response = await trends(`/top_trends/?date=${date}${platformFilter}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_TREND_TOP_TRACKS,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      "Error fetching trend top tracks",
      dispatch
    )
  }

export const fetchStreamsTrackMonths =
  (filter: TrendFilter, offset: number, limit: number, selectedRoyaltyType?: KSelectOption) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const royaltyTypeParam = selectedRoyaltyType ? `&royalty_type=${selectedRoyaltyType.value2}` : ""
        const response = await trends(
          `/streams_track_months/${buildParams(filter)}&offset=${offset}&limit=${limit}${royaltyTypeParam}`,
          "get"
        )

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_TRACK_MONTHS,
          payload: {
            totals: mapListToCamelCase(response.data.totals),
            results: mapListToCamelCase(response.data.results),
            chartData: mapListToCamelCase(response.data.chart_data),
            totalStreams: response.data.total_streams,
            missingDays: response.data.missing_days || []
          }
        })
      },
      "Error fetching streams track months",
      dispatch
    )
  }

export const fetchArtistTrendEconomyData =
  (filter: TrendFilter, offset: number, limit: number, selectedRoyaltyType?: KSelectOption) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const royaltyTypeParam = selectedRoyaltyType ? `&royalty_type=${selectedRoyaltyType.value2}` : ""
        const response = await trends(
          `/artist_trend_economy/${buildParams(filter)}&offset=${offset}&limit=${limit}${royaltyTypeParam}`,
          "get"
        )

        dispatch({
          type: TRENDS_ACTION.FETCH_ARTIST_TREND_ECONOMIES,
          payload: {
            totals: mapListToCamelCase(response.data.totals),
            results: mapListToCamelCase(response.data.results),
            chartData: mapListToCamelCase(response.data.chart_data),
            totalRoyalty: response.data.total_royalty
          }
        })
      },
      "Error fetching artist trend economies",
      dispatch
    )
  }

export const fetchSummaryTrackRoyalties = (filter: TrendFilter, sort: SortParams) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const platformFilter = filter.platformList ? `&platforms=${filter.platformList.join(",")}` : ""
      const userFilter = filter.selectedUsers ? `&user_ids=${filter.selectedUsers.join(",")}` : ""

      const start = filter.dateValues?.[0]
        ? `${filter.dateValues?.[0].getFullYear()}${String(filter.dateValues?.[0].getMonth() + 1).padStart(2, "0")}`
        : ""
      const end = filter.dateValues?.[1]
        ? `${filter.dateValues?.[1].getFullYear()}${String(filter.dateValues?.[1].getMonth() + 1).padStart(2, "0")}`
        : ""

      const dateParam = start && end ? `&dates=${start},${end}` : ""

      let sortParam = ""
      if (sort.field) { 
        const direction = sort.direction === "asc" ? "" : "-"
        sortParam = `&order_by=${direction}${sort.field}`
      }

      const decliningParam = filter.declining ? "&declining=1" : ""
      const distributionTypeParam = filter.distributionType ? `&distribution_type=${filter.distributionType}` : ""

      const response = await trends(
        `/summary_track_royalties/?min_royalty=${filter.minRoyalty}${dateParam}${platformFilter}${userFilter}${sortParam}${decliningParam}${distributionTypeParam}`,
        "get"
      )

      dispatch({
        type: TRENDS_ACTION.FETCH_SUMMARY_TRACK_ROYALTIES,
        payload: mapListToCamelCase(response.data)
      })
    },
    "Error fetching summary track royalties",
    dispatch
  )
}

export const fetchSummaryTrackStreams = (filter: TrendFilter, sort: SortParams) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const platformFilter = filter.platformList ? `&platforms=${filter.platformList.join(",")}` : ""
      const userFilter = filter.selectedUsers ? `&user_ids=${filter.selectedUsers.join(",")}` : ""

      const start = filter.dateValues?.[0]
        ? `${filter.dateValues?.[0].getFullYear()}${String(filter.dateValues?.[0].getMonth() + 1).padStart(2, "0")}${String(filter.dateValues?.[0].getDate()).padStart(2, "0")}`
        : ""
      const end = filter.dateValues?.[1]
        ? `${filter.dateValues?.[1].getFullYear()}${String(filter.dateValues?.[1].getMonth() + 1).padStart(2, "0")}${String(filter.dateValues?.[1].getDate()).padStart(2, "0")}`
        : ""

      const dateParam = start && end ? `&dates=${start},${end}` : ""

      let sortParam = ""
      if (sort.field) { 
        const direction = sort.direction === "asc" ? "" : "-"
        sortParam = `&order_by=${direction}${sort.field}`
      }

      const response = await trends(
        `/summary_track_streams/?min_stream=${filter.minStream}${dateParam}${platformFilter}${userFilter}${sortParam}`,
        "get"
      )

      dispatch({
        type: TRENDS_ACTION.FETCH_SUMMARY_TRACK_STREAMS,
        payload: mapListToCamelCase(response.data)
      })
    },
    "Error fetching summary track royalties",
    dispatch
  )
}
